import {css} from 'aphrodite';
import React from 'react';

import {LeafType} from 'data-types/benchling-type-def/BenchlingTypeDef';
import {ButtonSize} from 'ui-library/buttons/Button';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import {IntermediateValueControlledNumericInput} from 'ui-library/inputs/ControlledNumericInput';
import TextInput from 'ui-library/inputs/TextInput';
import Menu from 'ui-library/menus/Menu';
import {MenuHeight, MenuWidth} from 'ui-library/menus/MenuConstants';
import Option from 'ui-library/menus/Option';
import {NumericInputChangeEvent} from 'util/Types';

import {BENCHLING_TYPE_INPUT_STYLES} from './BenchlingTypeValueInputConstants';
import {ValueInputProps} from './ValueInputTypes';

/**
 * @package
 */
export function DecimalValueInput(props: ValueInputProps<LeafType.DECIMAL>): JSX.Element {
  const value =
    typeof props.valueInput.value === 'string' || typeof props.valueInput.value === 'number'
      ? props.valueInput.value
      : null;

  return (
    <IntermediateValueControlledNumericInput
      className={css(BENCHLING_TYPE_INPUT_STYLES.primitiveValueInput)}
      focusOnMount={props.allowAutoFocus}
      onChange={(e: NumericInputChangeEvent) => {
        props.onChange(e.target.numericValue ?? (e.target.value !== '' ? e.target.value : null));
      }}
      placeholder="Decimal"
      value={value}
      validateInteger={false}
      isDisabled={props.disabled}
    />
  );
}

/**
 * @package
 */
export function IntegerValueInput(props: ValueInputProps<LeafType.INTEGER>): JSX.Element {
  const value =
    typeof props.valueInput.value === 'string' || typeof props.valueInput.value === 'number'
      ? props.valueInput.value
      : null;

  return (
    <IntermediateValueControlledNumericInput
      className={css(BENCHLING_TYPE_INPUT_STYLES.primitiveValueInput)}
      focusOnMount={props.allowAutoFocus}
      onChange={(e: NumericInputChangeEvent) => {
        props.onChange(e.target.numericValue ?? (e.target.value !== '' ? e.target.value : null));
      }}
      placeholder="Integer"
      value={value}
      validateInteger={true}
      isDisabled={props.disabled}
    />
  );
}

/**
 * @package
 */
export function TextValueInput(props: ValueInputProps<LeafType.TEXT>): JSX.Element {
  const value =
    typeof props.valueInput.value === 'string' || typeof props.valueInput.value === 'number'
      ? props.valueInput.value
      : null;

  return (
    <TextInput
      autoFocus={props.allowAutoFocus}
      // Coerce null to '' for display
      value={value ?? ''}
      className={css(BENCHLING_TYPE_INPUT_STYLES.primitiveValueInput)}
      onChange={(e) => {
        const newStringValue = e.target.value;
        props.onChange(newStringValue);
      }}
      placeholder="Text"
      disabled={props.disabled}
    />
  );
}

/**
 * @package
 */
export function BooleanValueInput(props: ValueInputProps<LeafType.BOOLEAN>): JSX.Element {
  const value = props.valueInput.isValid ? props.valueInput.value : true;

  return (
    <SelectDropdown
      buttonSize={ButtonSize.MEDIUM}
      buttonClassName={css(BENCHLING_TYPE_INPUT_STYLES.primitiveValueInput)}
      disabled={props.disabled}
      displayName={value ? 'True' : 'False'}
      menu={
        <Menu height={MenuHeight.MEDIUM} width={MenuWidth.LARGE}>
          <Option
            key="true"
            value={true}
            onSelect={() => {
              props.onChange(true);
            }}
          >
            True
          </Option>
          <Option
            key="false"
            value={false}
            onSelect={() => {
              props.onChange(false);
            }}
          >
            False
          </Option>
        </Menu>
      }
    />
  );
}
